import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Activity, AlertTriangle, CheckCircle, Clock, TrendingUp, Zap } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ParsingMetrics {
  total_attempts: number;
  successful_parses: number;
  failed_parses: number;
  avg_confidence: number;
  avg_processing_time: number;
  provider_performance: Array<{
    provider: string;
    attempts: number;
    success_rate: number;
    avg_confidence: number;
    cost_estimate: number;
  }>;
  hourly_stats: Array<{
    hour: string;
    attempts: number;
    success_rate: number;
  }>;
}

const AdvancedParsingAnalytics = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');

  // Fetch parsing analytics
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['parsing-analytics', selectedTimeRange],
    queryFn: async () => {
      const response = await supabase.functions.invoke('get-parsing-analytics', {
        body: { timeRange: selectedTimeRange }
      });
      return response.data as ParsingMetrics;
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'openai': return '🤖';
      case 'mistral': return '🌟';
      case 'openrouter': return '🛤️';
      case 'deepseek': return '🌊';
      default: return '⚡';
    }
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 80) return 'default';
    if (rate >= 60) return 'secondary';
    return 'destructive';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-3/4"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <Card>
        <CardContent className="pt-6 text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">No parsing analytics available</p>
        </CardContent>
      </Card>
    );
  }

  const successRate = metrics.total_attempts > 0 
    ? (metrics.successful_parses / metrics.total_attempts) * 100 
    : 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Attempts</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.total_attempts}</div>
            <p className="text-xs text-muted-foreground">
              +12% from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{successRate.toFixed(1)}%</div>
            <Progress value={successRate} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Confidence</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.avg_confidence.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              Target: 85%+
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Processing Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.avg_processing_time.toFixed(1)}s</div>
            <p className="text-xs text-muted-foreground">
              Target: &lt;30s
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="providers" className="space-y-4">
        <TabsList>
          <TabsTrigger value="providers">Provider Performance</TabsTrigger>
          <TabsTrigger value="trends">Hourly Trends</TabsTrigger>
          <TabsTrigger value="costs">Cost Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="providers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>AI Provider Performance</CardTitle>
              <CardDescription>
                Performance metrics for each AI parsing provider
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {metrics.provider_performance.map((provider) => (
                  <div key={provider.provider} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getProviderIcon(provider.provider)}</span>
                      <div>
                        <h4 className="font-medium capitalize">{provider.provider}</h4>
                        <p className="text-sm text-muted-foreground">
                          {provider.attempts} attempts
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="font-medium">{provider.success_rate.toFixed(1)}%</div>
                        <div className="text-sm text-muted-foreground">Success Rate</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">{provider.avg_confidence.toFixed(1)}%</div>
                        <div className="text-sm text-muted-foreground">Avg Confidence</div>
                      </div>
                      <Badge variant={getSuccessRateColor(provider.success_rate)}>
                        {provider.success_rate >= 80 ? 'Excellent' : 
                         provider.success_rate >= 60 ? 'Good' : 'Needs Attention'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Hourly Processing Trends</CardTitle>
              <CardDescription>
                Parsing attempts and success rates by hour
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {metrics.hourly_stats.map((stat) => (
                  <div key={stat.hour} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center space-x-3">
                      <div className="font-medium">{stat.hour}:00</div>
                      <div className="text-sm text-muted-foreground">
                        {stat.attempts} attempts
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Progress value={stat.success_rate} className="w-24" />
                      <div className="text-sm font-medium w-12">
                        {stat.success_rate.toFixed(0)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="costs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cost Analysis</CardTitle>
              <CardDescription>
                Estimated costs per provider
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {metrics.provider_performance.map((provider) => (
                  <div key={provider.provider} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getProviderIcon(provider.provider)}</span>
                      <div>
                        <h4 className="font-medium capitalize">{provider.provider}</h4>
                        <p className="text-sm text-muted-foreground">
                          Cost per successful parse
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">${provider.cost_estimate.toFixed(4)}</div>
                      <div className="text-sm text-muted-foreground">
                        Total: ${(provider.cost_estimate * provider.attempts).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdvancedParsingAnalytics;